import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { TransferWorkshopStoreDetailModel } from 'src/app/models/maintenance/transfer-workshop-store-detail.model';
import { TransferWorkshopStoreModel } from 'src/app/models/maintenance/transfer-workshop-store.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { TransferWorkshopStoreDetailService } from 'src/app/services/maintenance/transfer-workshop-store-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-transfer-workshop-store-detail',
  templateUrl: './register-transfer-workshop-store-detail.component.html',
  styleUrls: ['./register-transfer-workshop-store-detail.component.scss'],
  standalone: false
})
export class RegisterTransferWorkshopStoreDetailComponent implements OnChanges {

  @Input() transferWorkshopStoreDetailContract: TransferWorkshopStoreDetailModel = new TransferWorkshopStoreDetailModel();
  @Input() transferWorkshopStoreModel: TransferWorkshopStoreModel = new TransferWorkshopStoreModel();
  @Input() transferWorkshopStoreDetails: TransferWorkshopStoreDetailModel[] = [];
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  codeNameArticle: string = '';
  codeNameStore: string = '';
  formTransferWorkshopStoreDetail: FormGroup = new FormGroup({});
  transferWorkshopStoreDetailId: number = 0;
  stockArticle: number = 0;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private transferWorkshopStoreDetailService: TransferWorkshopStoreDetailService,
    private storeService: StoreService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new TransferWorkshopStoreDetailModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    const transferWorkshopStoreDetails = changes["transferWorkshopStoreDetails"];
    const transferWorkshopStoreDetailContract = changes["transferWorkshopStoreDetailContract"];
    if (transferWorkshopStoreDetails && !transferWorkshopStoreDetails.firstChange) {
      await this.createTransferWorkshopStoreDetails(this.transferWorkshopStoreDetails);
      return;
    }
    if (transferWorkshopStoreDetailContract && !transferWorkshopStoreDetailContract.firstChange) {
      if (this.transferWorkshopStoreDetailContract.transferWorkshopStoreDetailId == 0) {
        this.createNewDetail();
      } else {
        this.transferWorkshopStoreDetailId = this.transferWorkshopStoreDetailContract.transferWorkshopStoreDetailId;
        this.validateForm(this.transferWorkshopStoreDetailContract);
      }
    }
  }

  async configureFormControls(transferWorkshopStoreDetail: TransferWorkshopStoreDetailModel) {
    const { storeId, articleId } = transferWorkshopStoreDetail;
    if (storeId && articleId) {
      await Promise.all([
        this.getStore(storeId),
        this.getArticle(articleId, storeId)
      ]);
    }
  }

  clearForm() {
    this.codeNameStore = '';
    this.codeNameArticle = '';
    this.transferWorkshopStoreDetailId = 0;
    this.isFormView.emit(false);
    this.validateForm(new TransferWorkshopStoreDetailModel());
  }

  createNewDetail() {
    this.codeNameStore = '';
    this.codeNameArticle = '';
    this.transferWorkshopStoreDetailId = 0;
    this.validateForm(this.transferWorkshopStoreDetailContract);
  }

  async createTransferWorkshopStoreDetails(transferWorkshopStoreDetails: TransferWorkshopStoreDetailModel[]) {
    try {
      await firstValueFrom(this.transferWorkshopStoreDetailService.createSomeDetails(transferWorkshopStoreDetails));
      this.toastHandlerService.showToast('success', 'Los detalles del traslado fueron creados exitosamente');
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles del traslado.', error);
    }
  }

  async createTransferWorkshopStoreDetail(modelTransferWorkshopStoreDetail: TransferWorkshopStoreDetailModel) {
    try {
      await firstValueFrom(this.transferWorkshopStoreDetailService.createAsync(modelTransferWorkshopStoreDetail));
      this.toastHandlerService.showToast('success', `El detalle del traslado fue creado exitosamente.`);
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear el detalle del traslado.', error);
    }
  }

  async getArticle(articleId: number, storeId: number) {
    try {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      this.codeNameArticle = `${article.code} - ${article.name}`;
      await this.getStocksByArticleIdStoreId(articleId, storeId);
    } catch (error) {
      console.error('Error al obtener el articulo por Id.', error);
      this.codeNameArticle = '';
    }
  }

  async getStocksByArticleIdStoreId(articleId: number, storeId: number) {
    const rentalStockModel = new RentalStockModel();
    rentalStockModel.articleId = articleId;
    rentalStockModel.storeId = storeId;
    rentalStockModel.dateMovement = this.transferWorkshopStoreModel.dateTransferWorkshopStore;
    const transferWorkshopStoreId = this.transferWorkshopStoreModel.transferWorkshopStoreId;
    try {
      const stock = await firstValueFrom(this.transferWorkshopStoreDetailService.getStocksByArticleIdStoreId(rentalStockModel,
        transferWorkshopStoreId));
      this.stockArticle = stock;
    } catch (error) {
      this.stockArticle = 0;
      console.error('Error al buscar las existencias por artículo y bodega.', error);
    }
  }

  async getStore(storeId: number) {
    try {
      const store = await firstValueFrom(this.storeService.getStoreById(storeId));
      this.codeNameStore = `${store.code} - ${store.name}`;
    } catch (error) {
      console.error('Error al obtener la bodega por Id.', error);
      this.codeNameStore = '';
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formTransferWorkshopStoreDetail.valid) {
      this.showValidationMessagesDetail();
      return;
    }
    const model = this.formTransferWorkshopStoreDetail.getRawValue() as TransferWorkshopStoreDetailModel;
    model.transferWorkshopStoreId = this.transferWorkshopStoreModel.transferWorkshopStoreId;
    if (this.transferWorkshopStoreDetailId == 0) {
      await this.createTransferWorkshopStoreDetail(model);
    } else {
      await this.updateTransferWorkshopStoreDetail(model);
    }
  }

  showValidationMessagesDetail() {
    const controlMessages: Record<string, Record<string, string>> = {
      transferWorkshopStoreId: {
        required: 'Debe seleccionar un Traslado válido.',
        min: 'Debe seleccionar un Traslado válido.'
      },
      articleId: {
        required: 'Debe seleccionar un Artículo.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      storeId: {
        required: 'Debe seleccionar una Bodega.',
        min: 'Debe seleccionar una Bodega válida.'
      },
      quantity: {
        required: 'Debe ingresar una Cantidad.',
        min: 'La Cantidad debe ser mayor a 0.'
      },
      costValue: {
        required: 'Debe ingresar un Costo.',
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formTransferWorkshopStoreDetail.get(controlName);
      if (control?.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateTransferWorkshopStoreDetail(modelTransferWorkshopStoreDetail: TransferWorkshopStoreDetailModel) {
    try {
      this.transferWorkshopStoreDetailContract.quantity = modelTransferWorkshopStoreDetail.quantity;
      this.transferWorkshopStoreDetailContract.costValue = modelTransferWorkshopStoreDetail.costValue;
      await firstValueFrom(this.transferWorkshopStoreDetailService.updateAsync(this.transferWorkshopStoreDetailContract));
      this.toastHandlerService.showToast('success', `El detalle del traslado fue modificado exitosamente.`);
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al modificar el detalle del traslado.', error);
    }
  }

  async validateForm(transferWorkshopStoreDetailModel: TransferWorkshopStoreDetailModel) {
    this.formTransferWorkshopStoreDetail = this.formBuilder.group({
      transferWorkshopStoreId: [transferWorkshopStoreDetailModel.transferWorkshopStoreId, [Validators.required, Validators.min(1)]],
      articleId: [transferWorkshopStoreDetailModel.articleId, [Validators.required, Validators.min(1)]],
      storeId: [transferWorkshopStoreDetailModel.storeId, [Validators.required, Validators.min(1)]],
      quantity: [transferWorkshopStoreDetailModel.quantity, [Validators.required, Validators.min(1)]],
      costValue: [transferWorkshopStoreDetailModel.costValue, [Validators.required, Validators.min(0)]],
    });
    await this.configureFormControls(transferWorkshopStoreDetailModel);
  }

  validateFormControls() {
    Object.keys(this.formTransferWorkshopStoreDetail.controls).forEach((key) => {
      const control = this.formTransferWorkshopStoreDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}